import { useClientPortalFlows } from '@/features/client-portal/hooks/use-portal-admin'
import type { PortalFlowV2 } from '@/features/client-portal/models/portal-form'
import { cn } from '@/shared/lib/utils'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/shared/ui/accordion'
import { validationTypeLabel } from '../consts/portal-flow-labels'
import {
  formatFlowDate,
  selectPreviousFlows,
  TERMINAL_FLOW_STATUSES
} from '../utils/portal-flow-selection'
import { FlowAuditTimeline } from './flow-audit-timeline'
import { PortalStatusBadge } from './portal-status-badge'

type PreviousFlowsPanelProps = {
  clientId: string
  clientName: string
  className?: string
}

/** Segunda línea de la fila: qué pasó con el flujo y cuándo. */
function flowOutcomeLine(flow: PortalFlowV2): string {
  if (flow.status === 'expired') return `Expiró el ${formatFlowDate(flow.expiresAt)}`
  if (TERMINAL_FLOW_STATUSES.has(flow.status))
    return `Finalizado el ${formatFlowDate(flow.updatedAt)}`
  return `Última actualización ${formatFlowDate(flow.updatedAt)}`
}

/**
 * "Flujos anteriores" del expediente: todos los flujos del cliente que no son el actual
 * (terminales y viejos), ordenados del más reciente al más antiguo. Cada fila expande
 * el timeline de auditoría de ese flujo. Complementa al `ActiveFlowPanel`, que muestra
 * el flujo vigente arriba y es dueño de los estados vacío/cargando del tab.
 */
export function PreviousFlowsPanel({ clientId, clientName, className }: PreviousFlowsPanelProps) {
  const { flows, isLoading } = useClientPortalFlows(clientId)

  const previous = selectPreviousFlows(flows ?? [])
  if (isLoading || previous.length === 0) return null

  return (
    <section className={cn('flex flex-col gap-1 border-t border-border/60 pt-4', className)}>
      <span className="text-xs font-medium uppercase text-muted-foreground">Flujos anteriores</span>
      <Accordion type="single" collapsible>
        {previous.map((flow) => (
          <AccordionItem key={flow.id} value={flow.id}>
            <AccordionTrigger className="py-3 hover:no-underline">
              <div className="flex flex-col items-start gap-1 text-left">
                <div className="flex flex-wrap items-center gap-2">
                  <PortalStatusBadge status={flow.status} />
                  <span className="text-xs text-muted-foreground">
                    {validationTypeLabel(flow.validationType)}
                  </span>
                </div>
                <span className="text-xs font-normal text-muted-foreground">
                  Creado el {formatFlowDate(flow.createdAt)} · {flowOutcomeLine(flow)}
                </span>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <FlowAuditTimeline flowId={flow.id} clientName={clientName} />
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </section>
  )
}
